import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, TrendingDown, Minus, ArrowRight, DollarSign,
  Calendar, BarChart3, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, LineChart, Line, ComposedChart, Area
} from 'recharts';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CostComparison = () => {
  const { shouldShowData } = useDemoData();

  const [period1, setPeriod1] = useState('this-month');
  const [period2, setPeriod2] = useState('last-month');
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComparisonData();
  }, [period1, period2]);

  const fetchComparisonData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/comparison?period1=${period1}&period2=${period2}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setComparisonData(data);
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Cost Comparison" description="Connect your cloud account to compare cost periods." />;
  }

  if (loading || !comparisonData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const data1 = comparisonData.period1;
  const data2 = comparisonData.period2;
  
  // Empty state when no data
  if (data1.total === 0 && data2.total === 0) {
    return (
      <div className="space-y-6" data-testid="cost-comparison-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-500" />
            Cost Comparison
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">Compare cloud costs across different time periods</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BarChart3 className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">No Cost Data Available</h3>
            <p className="text-slate-400 text-center max-w-md">Connect your cloud providers to compare costs across time periods.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const difference = data1.total - data2.total;
  const percentChange = data2.total > 0 ? ((difference / data2.total) * 100).toFixed(1) : '0.0';
  const isIncrease = difference > 0;

  // Prepare chart data
  const chartData = Object.keys(data1.breakdown).map(service => ({
    service,
    [data1.label]: data1.breakdown[service],
    [data2.label]: data2.breakdown[service] || 0,
    change: data1.breakdown[service] - (data2.breakdown[service] || 0)
  }));

  // Top changes
  const topChanges = chartData
    .map(item => ({
      service: item.service,
      change: item.change,
      percentChange: ((item.change / (item[data2.label] || 1)) * 100).toFixed(1)
    }))
    .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
    .slice(0, 5);

  // Trend data for line chart
  const trendData = [];

  return (
    <div className="space-y-6" data-testid="cost-comparison-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-500" />
            Cost Comparison
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Compare costs across different time periods
          </p>
        </div>
      </div>

      {/* Period Selectors */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <Select value={period1} onValueChange={setPeriod1}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="2-months-ago">2 Months Ago</SelectItem>
                  <SelectItem value="last-quarter">Last Quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <ArrowRight className="w-5 h-5 text-slate-400 hidden sm:block" />
            <span className="text-slate-400 sm:hidden">vs</span>
            
            <div className="flex items-center gap-3">
              <Select value={period2} onValueChange={setPeriod2}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="2-months-ago">2 Months Ago</SelectItem>
                  <SelectItem value="last-quarter">Last Quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-400 dark:text-slate-400">{data1.label}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
              ${data1.total.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-400 dark:text-slate-400">{data2.label}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
              ${data2.total.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className={`border-slate-200 dark:border-slate-800 ${
          isIncrease ? 'bg-red-50 dark:bg-red-900/10' : 'bg-emerald-50 dark:bg-emerald-900/10'
        }`}>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-400 dark:text-slate-400">Difference</p>
            <div className="flex items-center gap-2 mt-1">
              <p className={`text-3xl font-bold ${isIncrease ? 'text-red-600' : 'text-emerald-600'}`}>
                {isIncrease ? '+' : ''}{percentChange}%
              </p>
              {isIncrease 
                ? <ArrowUpRight className="w-6 h-6 text-red-500" />
                : <ArrowDownRight className="w-6 h-6 text-emerald-500" />
              }
            </div>
            <p className={`text-sm mt-1 ${isIncrease ? 'text-red-600' : 'text-emerald-600'}`}>
              {isIncrease ? '+' : ''}${difference.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Comparison */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Cost by Service</CardTitle>
            <CardDescription>Side-by-side comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis dataKey="service" type="category" width={100} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Bar dataKey={data1.label} fill="#10b981" />
                <Bar dataKey={data2.label} fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Cost Trend */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Cost Trend</CardTitle>
            <CardDescription>Last 5 months</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={trendData}>
                <defs>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Area type="monotone" dataKey="cost" fill="url(#colorCost)" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="cost" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', strokeWidth: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Changes */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Biggest Changes</CardTitle>
          <CardDescription>Services with the largest cost changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {topChanges.map((item, index) => (
              <div 
                key={item.service}
                className={`p-4 rounded-lg ${
                  item.change < 0 
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800' 
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs">#{index + 1}</Badge>
                  {item.change < 0 
                    ? <TrendingDown className="w-4 h-4 text-emerald-600" />
                    : <TrendingUp className="w-4 h-4 text-red-600" />
                  }
                </div>
                <h4 className="font-medium text-slate-900 dark:text-white text-sm truncate">{item.service}</h4>
                <p className={`text-lg font-bold mt-1 ${item.change < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {item.change < 0 ? '' : '+'}${Math.abs(item.change).toLocaleString()}
                </p>
                <p className={`text-xs ${item.change < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {item.change < 0 ? '' : '+'}{item.percentChange}%
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <Card className="border-slate-200 dark:border-slate-800 bg-slate-50 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
        <CardHeader>
          <CardTitle>AI Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {!isIncrease ? (
              <>
                <div className="flex items-start gap-3 p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-300">Costs are trending down!</p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      Your optimization efforts are paying off. Total spend decreased by ${Math.abs(difference).toLocaleString()} ({Math.abs(percentChange)}%).
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-emerald-100 dark:bg-blue-900/30 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800 dark:text-blue-300">Biggest savings: {topChanges[0]?.service}</p>
                    <p className="text-sm text-emerald-700 dark:text-blue-400">
                      This service contributed most to your cost reduction. Continue monitoring for further optimization opportunities.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-3 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-300">Costs are increasing</p>
                    <p className="text-sm text-red-700 dark:text-red-400">
                      Total spend increased by ${difference.toLocaleString()} ({percentChange}%). Review the top increasing services for optimization.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-300">Action recommended: Review {topChanges[0]?.service}</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      This service had the largest cost increase. Check for unused resources or consider reserved instances.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostComparison;
